import React from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { postSiteDeployment, getSite } from "@/lib/api";
import { Card as UICard, CardContent as UICardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import consumer from "@/lib/cable";

const SiteDeployments: React.FC = () => {
  const { siteSlug } = useParams<{ siteSlug: string }>();

  const mutation = useMutation({
    mutationFn: () => postSiteDeployment(siteSlug!),
    onSuccess: () => {
      toast.success("Deployment triggered!");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to trigger deployment");
    },
  });

  const [logs, setLogs] = React.useState<string[]>([]);
  const [deploymentState, setDeploymentState] = React.useState<string | null>(null);
  const [latestDeploy, setLatestDeploy] = React.useState<any>(null);
  const [deploymentTs, setDeploymentTs] = React.useState<number | null>(null);

  // Ref for log container to auto-scroll
  const logContainerRef = React.useRef<HTMLDivElement>(null);

  // Fetch site info on first load or when siteSlug changes
  React.useEffect(() => {
    if (!siteSlug) return;
    (async () => {
      try {
        const site = await getSite(siteSlug);
        setDeploymentState(site.deploy_state || null);
        setLatestDeploy(site.latest_deploy || null);
      } catch (err) {
        console.error("Failed to fetch site data on mount", err);
      }
    })();
  }, [siteSlug, deploymentTs]);

  // Auto-scroll log container to bottom when logs change
  React.useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  React.useEffect(() => {
    if (!siteSlug) return;
    // Subscribe to deployments_channel for this site
    const subscription = (consumer as any).subscriptions.create(
      { channel: "DeploymentsChannel", site_slug: siteSlug },
      {
        received: async (data: any) => {
          console.log("Received data:", data);
          if (data && data.log) {
            setLogs((prev) => [...prev, data.log]);
          }
          if(data.type == "state"){
            setDeploymentTs(Date.now());
          }
          if (data && data.type === "deploy_submit" && siteSlug) {
            try {
              const site = await getSite(siteSlug);
              setDeploymentState(site.deploy_state || null);
              setLatestDeploy(site.latest_deploy || null);
            } catch (err) {
              console.error("Failed to fetch site data after deploy_submit", err);
            }
          }
        },
      }
    );
    return () => {
      if (subscription && typeof subscription.unsubscribe === "function") {
        subscription.unsubscribe();
      }
    };
  }, [siteSlug]);

  return (
    <div className="p-8">
      <Button
        onClick={() => mutation.mutate()}
        disabled={mutation.status === "pending"}
        className="bg-black text-white"
      >
        {mutation.status === "pending" ? "Deploying..." : "Trigger Deployment"}
      </Button>

      {/* Deployment State & Latest Deploy Block */}

      {(deploymentState || latestDeploy) && (
        <div className="mt-6 mb-4 space-y-4">
          {deploymentState && (
            <span
              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mr-2
                ${
                  deploymentState === "PENDING"
                    ? "bg-gray-100 text-gray-800"
                    : deploymentState === "ERROR"
                    ? "bg-red-100 text-red-800"
                    : deploymentState === "QUEUED"
                    ? "bg-yellow-100 text-yellow-800"
                    : deploymentState === "READY"
                    ? "bg-green-100 text-green-800"
                    : "bg-green-100 text-green-800"
                }
              `}
            >
              {deploymentState}
            </span>
          )}
          {latestDeploy && (
            <UICard className="bg-white border shadow rounded-lg">
              <UICardContent className="p-4">
                <div className="mb-2 flex flex-wrap gap-4 items-center">
                  <span className="font-semibold text-sm text-zinc-700">
                    Status:
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-bold
                    ${
                      latestDeploy.status === "READY"
                        ? "bg-green-100 text-green-800"
                        : latestDeploy.status === "ERROR"
                        ? "bg-red-100 text-red-800"
                        : latestDeploy.status === "QUEUED"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }
                  `}>
                    {latestDeploy.status}
                  </span>
                  <span className="font-semibold text-sm text-zinc-700">
                    Ready State:
                  </span>
                  <span className="px-2 py-1 rounded text-xs bg-zinc-100 text-zinc-800 font-mono">
                    {latestDeploy.readyState}
                  </span>
                  {latestDeploy.url && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">URL:</span>
                      <a
                        href={`https://${latestDeploy.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-xs break-all"
                      >
                        {latestDeploy.url}
                      </a>
                    </>
                  )}

                  {latestDeploy.inspectorUrl && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">Inspector URL:</span>
                      <a
                        href={`${latestDeploy.inspectorUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-xs break-all"
                      >
                        Inspector URL
                      </a>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 items-center mb-2">
                  {latestDeploy.project?.name && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">Project:</span>
                      <span className="text-xs text-zinc-800">{latestDeploy.project.name}</span>
                    </>
                  )}
                  {latestDeploy.team?.name && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">Team:</span>
                      <span className="text-xs text-zinc-800">{latestDeploy.team.name}</span>
                    </>
                  )}
                  {latestDeploy.target && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">Target:</span>
                      <span className="text-xs text-zinc-800">{latestDeploy.target}</span>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 items-center mb-2">
                  {latestDeploy.createdAt && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">Created:</span>
                      <span className="text-xs text-zinc-800">
                        {new Date(latestDeploy.createdAt).toLocaleString()}
                      </span>
                    </>
                  )}
                  {latestDeploy.bootedAt && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">Booted:</span>
                      <span className="text-xs text-zinc-800">
                        {new Date(latestDeploy.bootedAt).toLocaleString()}
                      </span>
                    </>
                  )}
                  {latestDeploy.buildingAt && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">Building:</span>
                      <span className="text-xs text-zinc-800">
                        {new Date(latestDeploy.buildingAt).toLocaleString()}
                      </span>
                    </>
                  )}
                  {latestDeploy.ready && (
                    <>
                      <span className="font-semibold text-sm text-zinc-700">Ready:</span>
                      <span className="text-xs text-zinc-800">
                        {new Date(latestDeploy.ready).toLocaleString()}
                      </span>
                    </>
                  )}
                </div>
                {/* Accordion for full JSON */}
                <Accordion type="single" collapsible className="mt-4">
                  <AccordionItem value="json">
                    <AccordionTrigger className="text-xs font-semibold">
                      Show full deployment JSON
                    </AccordionTrigger>
                    <AccordionContent>
                      <pre className="bg-zinc-900 text-xs text-zinc-100 rounded p-2 overflow-x-auto">
                        {JSON.stringify(latestDeploy, null, 2)}
                      </pre>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </UICardContent>
            </UICard>
          )}
        </div>
      )}

      <Card className="mt-6 bg-black border-none shadow-inner rounded-lg p-0">
        {/* Browser-like header */}
        <div className="flex items-center h-8 px-4 bg-zinc-900 rounded-t-lg border-b border-zinc-800">
          <div className="flex space-x-2">
            <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span>
            <span className="w-3 h-3 rounded-full bg-yellow-400 inline-block"></span>
            <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
          </div>
        </div>
        <CardContent
          className="p-4 min-h-[200px] max-h-[400px] overflow-y-auto font-mono text-green-400 bg-black rounded-b-lg"
          ref={logContainerRef}
        >
          <div className="font-bold text-white mb-2">Deployment Console</div>
          {logs.length === 0 ? (
            <div className="text-gray-400">No deployment logs yet.</div>
          ) : (
            logs.map((line, idx) => (
              <div key={idx} className="whitespace-pre-wrap">{line}</div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SiteDeployments;
